import { Builder } from '@builder.io/react';
import React, { useMemo, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { twMerge } from 'tailwind-merge';

import ChevronDown from '@videoblocks/react-icons/ChevronDown';

import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import { generateFAQSchema } from '../../utils/structured-data/faqSchemaGenerator';
import StructuredData from '../elements/StructuredData';

import './FAQSeo.less';

interface IFaqItem {
  faqQuestion: string;
  faqAnswer: string;
}

type TitleHeadingLevel = 'h2' | 'h3';

interface IFaqSeoProps {
  faqItems?: IFaqItem[];
  showButton?: boolean;
  buttonLabel?: string;
  buttonUrl?: string;
  collapseItems?: boolean;
  titleHeadingLevel?: TitleHeadingLevel;
}

const DEFAULT_BUTTON_URL = 'https://help.storyblocks.com/en';
const VISIBLE_ITEMS_WHEN_COLLAPSED = 5;
const DEFAULT_TITLE_HEADING_LEVEL: TitleHeadingLevel = 'h2';

// Questions sit one level below the title, so the outline has no skipped levels
const QUESTION_HEADING_LEVEL: Record<TitleHeadingLevel, 'h3' | 'h4'> = {
  h2: 'h3',
  h3: 'h4',
};

const sanitizeConfig: sanitizeHtml.IOptions = {
  allowedTags: ['p', 'ul', 'ol', 'li', 'a', 'strong', 'span', 'em'],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
};

const FaqSeo = ({
  faqItems = [],
  showButton = true,
  buttonLabel = 'See All FAQs',
  buttonUrl = DEFAULT_BUTTON_URL,
  collapseItems = false,
  titleHeadingLevel = DEFAULT_TITLE_HEADING_LEVEL,
}: IFaqSeoProps) => {
  const TitleHeading = titleHeadingLevel;
  const QuestionHeading = QUESTION_HEADING_LEVEL[titleHeadingLevel];
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasCollapsedItems =
    collapseItems && faqItems.length > VISIBLE_ITEMS_WHEN_COLLAPSED;

  const faqStructuredData = useMemo(() => {
    if (!faqItems.length) return null;
    return generateFAQSchema({
      faqItems,
      title: 'Frequently Asked Questions',
      organizationName: 'Storyblocks',
    });
  }, [faqItems]);

  const handleOnToggle = (section: string) => {
    setOpenItem((prev) => (prev === section ? null : section));
  };

  if (!faqItems.length) return null;

  return (
    <>
      {faqStructuredData && <StructuredData data={faqStructuredData} />}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div
          className="font-polysans flex flex-col lg:flex-row w-full mx-auto gap-8"
          style={{ maxWidth: '1440px' }}
        >
          <div className="lg:w-1/3 lg:flex-none">
            {/* The level is configurable so the FAQ fits the outline of the page that embeds it */}
            <Text
              fontFamily="polysans"
              fontSize="4xl"
              fontWeight="semibold"
              className="text-3xl md:text-4xl mt-0"
              asChild
            >
              <TitleHeading>
                <span className="lg:block">Frequently Asked Questions</span>
              </TitleHeading>
            </Text>
          </div>

          <div className="flex-auto flex flex-col items-center">
            <ul className="w-full space-y-6 mb-0">
              {faqItems.map(({ faqAnswer, faqQuestion }, index) => {
                const isOpen = openItem === `faq_${index}`;
                return (
                  <li
                    className={twMerge(
                      'bg-gray-200 rounded-3xl border-gray-300 border-2',
                      'flex flex-col w-full text-left',
                      'transition-colors duration-150',
                      'active:bg-gray-300 active:border-gray-500',
                      // Hidden with CSS rather than not rendered, so crawlers still read every answer
                      hasCollapsedItems &&
                        !isExpanded &&
                        index >= VISIBLE_ITEMS_WHEN_COLLAPSED &&
                        'hidden'
                    )}
                    key={`faq_${index}`}
                  >
                    {/* Accordion pattern: a native button inside the heading, so the row is
                        keyboard operable and the answer's links aren't nested in an interactive element */}
                    <QuestionHeading className="my-0">
                      <Text
                        className={twMerge(
                          'text-gray-1000 text-xl leading-7 lg:text-2xl lg:leading-normal',
                          'flex w-full justify-between items-start gap-4 text-left',
                          'bg-transparent border-0 m-0 rounded-3xl',
                          'px-6 pt-6 lg:px-8 lg:pt-8',
                          isOpen ? 'pb-0' : 'pb-6 lg:pb-8',
                          'cursor-pointer touch-manipulation',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600'
                        )}
                        fontFamily="polysans"
                        fontSize="2xl"
                        fontWeight="semibold"
                        asChild
                      >
                        <button
                          type="button"
                          id={`faq-question-${index}`}
                          aria-expanded={isOpen}
                          aria-controls={`faq-content-${index}`}
                          onClick={() => handleOnToggle(`faq_${index}`)}
                        >
                          <span>{faqQuestion}</span>
                          <span
                            className="flex flex-none justify-center items-center w-8 h-8 bg-yellow-300 rounded-full border-2 border-blue-900 transition-transform duration-200"
                            aria-hidden="true"
                          >
                            <ChevronDown
                              className={twMerge(
                                'h-4 w-4 transition-transform duration-200 ease-in-out',
                                isOpen && 'transform rotate-180'
                              )}
                            />
                          </span>
                        </button>
                      </Text>
                    </QuestionHeading>
                    <div
                      id={`faq-content-${index}`}
                      className={twMerge(
                        isOpen
                          ? 'max-h-[1000px] px-6 pt-4 pb-6 lg:px-8 lg:pb-8'
                          : // invisible takes a closed answer's links out of the tab order; the text stays in the HTML
                            'max-h-0 px-6 lg:px-8 invisible',
                        'overflow-hidden transition-[max-height] duration-200 ease-in-out'
                      )}
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                    >
                      <div
                        className="faq-seo-answer text-gray-800 font-polysans text-base leading-snug lg:text-xl lg:leading-[27px]"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(faqAnswer, sanitizeConfig),
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            {hasCollapsedItems && (
              <button
                type="button"
                className="text-blue-700 p-4 text-base text-center"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
              >
                {isExpanded ? 'Hide FAQs' : 'Show All FAQs'}
              </button>
            )}
            {showButton && (
              <Button
                href={buttonUrl}
                variant="secondary"
                size="large"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 md:mt-12"
              >
                {buttonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FaqSeo;

Builder.registerComponent(FaqSeo, {
  name: 'Faq SEO',
  inputs: [
    {
      name: 'faqItems',
      type: 'list',
      required: true,
      helperText: 'List of questions and answers',
      defaultValue: [
        {
          faqQuestion: 'Question',
          faqAnswer: 'Answer',
        },
      ],
      subFields: [
        {
          name: 'faqQuestion',
          type: 'string',
          required: true,
          helperText: 'Question',
          defaultValue: 'Question',
        },
        {
          name: 'faqAnswer',
          type: 'richText',
          required: true,
          helperText: 'Answer (supports rich text)',
          defaultValue: 'Answer',
        },
      ],
    },
    {
      name: 'showButton',
      type: 'boolean',
      helperText: 'Show or hide the "See All FAQs" button',
      defaultValue: true,
    },
    {
      name: 'buttonLabel',
      type: 'string',
      helperText: 'Button label',
      defaultValue: 'See All FAQs',
      showIf: `options.get('showButton') === true`,
    },
    {
      name: 'buttonUrl',
      type: 'url',
      helperText:
        'Button destination URL (supports additional query parameters)',
      defaultValue: 'https://help.storyblocks.com/en',
      showIf: `options.get('showButton') === true`,
    },
    {
      name: 'titleHeadingLevel',
      type: 'string',
      helperText:
        'Heading level of the title; questions use the next level. Match the sections around the FAQ',
      defaultValue: DEFAULT_TITLE_HEADING_LEVEL,
      enum: [
        { label: 'Title H2, questions H3', value: 'h2' },
        { label: 'Title H3, questions H4', value: 'h3' },
      ],
    },
  ],
});
